import React, { useEffect, useState, Fragment } from 'react';
import { Container, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string, mixed, date } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { CheckboxInput, TextInput, SelectInput, RadioInput } from '../../../../Helpers/Inputs';
import { InputMaskPhone, InputMaskSSN, InputMaskDate } from '../../../../Helpers/Formatters';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import moment from 'moment';
import States from '../../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../../Helpers/Lists/StatesWithDisabled';
import BackNext from '../../../Common/SubComponents/BackNext';
import IsJuvenile from '../../../../Helpers/Dates/IsJuvenile';
import GenderButtons from '../../../Common/SubComponents/GenderButtons';
import { emptyStringToUndefined } from '../../../../Helpers/Formatters/EmptyStringToUndefined.jsx';
import { customValidators } from '../../../../Helpers/Validators/customValidators'
import Age from '../../../../Helpers/Dates/Age'
import County from '../../../../Helpers/Lists/County.jsx';

export default function InsuredInfo(props) {
  const isJuvenile = IsJuvenile(props.basicInfo.birthdate);
  var currentAge = Age(props.basicInfo.birthdate);
  const formMethods = useForm({
    resolver: yupResolver(InsuredInfo.ValidationSchema(currentAge)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });

  const { handleSubmit, formState: { errors }, trigger, register, setValue } = formMethods;
  useEffect(() => {
    register("isJuvenile");
    setValue("isJuvenile", isJuvenile);
  }, [register]);

  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const [validFields, setValidFields] = useState({
    firstName: false,
    lastName: false,
    state: false,
    phone: false,
    email: false,
  });

  const validateForm = async (field) => {
    setValidFields({
      ...validFields,
      [field]: await trigger(field),
    });
  }

  useEffect(() => {
    if (Object.values(validFields).reduce((a, b) => a && b, true) && props.onFormComplete) {
      props.onFormComplete();
    }
  }, [validFields]);

  const handleChangeState = (field, value, driversLicense) => {
    props.onFieldChange(field, value);
    trigger(field);
    if (driversLicense !== "") {
      trigger('driversLicense');
    }
  };
  useEffect(() => {
    const firstName = props.formState.firstName || props.basicInfo.firstName || '';
    const lastName = props.formState.lastName || props.basicInfo.lastName || '';
    const email = props.formState.email || props.basicInfo.email;
    const state = props.formState.state || props.basicInfo.state;
    const phone = props.formState.phone || props.basicInfo.phone;
    const birthdate = props.formState.ownerBirthdate || props.basicInfo.birthdate;
    const gender = props.formState.ownerGender || props.basicInfo.gender;

    setValue('firstName', firstName);
    setValue('lastName', lastName);
    setValue('email', email);
    setValue('state', state);
    setValue('phone', phone);
    setValue('birthdate', birthdate);
    setValue('gender', gender);

    var form = { ...props.fullFormState };
    form.pages.insuredInfo.firstName = firstName;
    form.pages.insuredInfo.lastName = lastName;
    form.pages.insuredInfo.email = email;
    form.pages.insuredInfo.state = state;
    form.pages.insuredInfo.phone = phone;
    form.pages.insuredInfo.ownerBirthdate = birthdate;
    form.pages.insuredInfo.ownerGender = gender;

    props.onMultiFieldChange(form);
  }, []);

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={5}>
          <FormProvider {...formMethods}>
            <form className="quote-form" onSubmit={handleSubmit(props.onNext)}>
              <div className="mb-3">
                <CheckboxInput
                  name="agreeToTerms"
                  id="agreeToTerms"
                  checked={props.formState.agreeToTerms}
                  onChange={(e) => props.onFieldChange('agreeToTerms', e.target.checked)}
                  label="By starting your application you have reviewed our privacy policy and have read and understand our privacy policy and agree to its terms and conditions."
                />
                <a href="/documents/SecurianPrivacyPolicy.pdf" target="_blank">
                  Privacy Policy
                </a>
              </div>

              <div className="formPageTitle">{isJuvenile === true ? "Owner Information (Parent/Guardian)" : "Owner/Proposed Insured Information"}</div>

              <div className="mb-3">
                <TextInput
                  name="firstName"
                  id="firstName"
                  label="First Name"
                  value={props.formState.firstName}
                  onChange={(e) => props.onFieldChange('firstName', e.target.value)}
                  onBlur={() => validateForm("firstName")}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="middleName"
                  id="middleName"
                  label="Middle Name"
                  value={props.formState.middleName}
                  onChange={(e) => props.onFieldChange('middleName', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="lastName"
                  id="lastName"
                  label="Last Name"
                  value={props.formState.lastName}
                  onChange={(e) => props.onFieldChange('lastName', e.target.value)}
                  onBlur={() => validateForm("lastName")}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="streetAddress"
                  id="streetAddress"
                  label="Street Address"
                  value={props.formState.streetAddress}
                  onChange={(e) => props.onFieldChange('streetAddress', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="apartment"
                  id="apartment"
                  label="Apt or Suite (Optional)"
                  value={props.formState.apartment}
                  onChange={(e) => props.onFieldChange('apartment', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="city"
                  id="city"
                  label="City"
                  value={props.formState.city}
                  onChange={(e) => props.onFieldChange('city', e.target.value)}
                />
              </div>
              <div className="mb-3">
                <SelectInput
                  name="county"
                  id="county"
                  value={props.formState.county}
                  onChange={(e) => handleValueChange('county', e.target.value)}
                  label={props.formState.state === "FL" ? "County" : "County (Optional)"}
                  selectOptions={County()}
                />
              </div>

              <Row>
                <Col>
                  <div className="mb-3">
                    <SelectInput
                      name="state"
                      id="state"
                      value={props.formState.state}
                      onChange={(e) => handleValueChange('state', e.target.value)}
                      onBlur={() => validateForm("state")}
                      label="State"
                      selectOptions={StatesWithDisabled(props.unavailableStates)}
                    />
                  </div>
                </Col>
                <Col>
                  <div className="mb-3">
                    <TextInput
                      name="zip"
                      id="zip"
                      label="Zip"
                      value={props.formState.zip}
                      onChange={(e) => props.onFieldChange('zip', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              <div className="mb-3">
                <TextInput
                  name="phone"
                  id="phone"
                  label="Daytime Phone"
                  value={props.formState.phone}
                  onChange={(e) => props.onFieldChange('phone', e.target.value)}
                  onBlur={() => validateForm("phone")}
                  InputProps={{
                    inputComponent: InputMaskPhone,
                  }}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="socialSecurityNumber"
                  id="socialSecurityNumber"
                  label="Social Security Number"
                  value={props.formState.socialSecurityNumber}
                  onChange={(e) => props.onFieldChange('socialSecurityNumber', e.target.value)}
                  InputProps={{
                    inputComponent: InputMaskSSN,
                  }}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="email"
                  id="email"
                  label="Email"
                  value={props.formState.email}
                  onChange={(e) => props.onFieldChange('email', e.target.value)}
                  onBlur={() => validateForm("email")}
                />
              </div>

              {isJuvenile ? (
                <Fragment>
                  <GenderButtons
                    name="ownerGender"
                    selectedGender={props.formState.ownerGender}
                    onClickMale={() => handleValueChange('ownerGender', 'M')}
                    onClickFemale={() => handleValueChange('ownerGender', 'F')}
                    error={errors.ownerGender}
                  />
                  <div className="mb-3">
                    <TextInput
                      name="ownerBirthdate"
                      id="ownerBirthdate"
                      label="Date of Birth"
                      value={props.formState.ownerBirthdate}
                      inputRef={props.register}
                      onChange={(e) => handleValueChange('ownerBirthdate', e.target.value)}
                      onBlur={() => validateForm("ownerBirthdate")}
                      InputProps={{
                        inputComponent: InputMaskDate,
                      }}
                    />
                  </div>
                </Fragment>
              ) : (
                <Fragment>
                  <div className="mb-3">
                    <SelectInput
                      name="driversLicenseState"
                      id="driversLicenseState"
                      value={props.formState.driversLicenseState}
                      onChange={(e) => handleChangeState('driversLicenseState', e.target.value)}
                      label="Driver's License State"
                      selectOptions={States()}
                    />
                  </div>
                  <Row>
                    <Col className="col-xl-6 col-md-12 col-sm-12 col-12">
                      <div className="mb-3">
                        <TextInput
                          name="driversLicense"
                          id="driversLicense"
                          label="Driver's License Number"
                          value={props.formState.driversLicense}
                          onChange={(e) => handleValueChange('driversLicense', e.target.value)}
                          disabled={props.formState.driversLicenseState === ""}
                        />
                      </div>
                    </Col>
                    <Col className="col-xl-6 col-md-12 col-sm-12 col-12">
                      <div className="mb-3">
                        <TextInput
                          name="driversLicenseExpiration"
                          id="driversLicenseExpiration"
                          label="Expiration Date"
                          value={props.formState.driversLicenseExpiration}
                          onChange={(e) => props.onFieldChange('driversLicenseExpiration', e.target.value)}
                          InputProps={{
                            inputComponent: InputMaskDate,
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Fragment>
              )}
              {isJuvenile === true && (
                <Fragment>
                  <div className="formPageTitle">Proposed Insured Information (Juvenile)</div>
                  <div className="mb-3">
                    <TextInput
                      name="childFirstName"
                      id="childFirstName"
                      label="First Name"
                      value={props.formState.childFirstName}
                      onChange={(e) => props.onFieldChange('childFirstName', e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <TextInput
                      name="childMiddleName"
                      id="childMiddleName"
                      label="Middle Name"
                      value={props.formState.childMiddleName}
                      onChange={(e) => props.onFieldChange('childMiddleName', e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <TextInput
                      name="childLastName"
                      id="childLastName"
                      label="Last Name"
                      value={props.formState.childLastName}
                      onChange={(e) => props.onFieldChange('childLastName', e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <TextInput
                      name="childSocialSecurityNumber"
                      id="childSocialSecurityNumber"
                      label="Social Security Number"
                      value={props.formState.childSocialSecurityNumber}
                      onChange={(e) => props.onFieldChange('childSocialSecurityNumber', e.target.value)}
                      InputProps={{
                        inputComponent: InputMaskSSN,
                      }}
                    />
                  </div>

                  <div className="mb-3">
                    <RadioInput
                      name="legalGuardian"
                      id="legalGuardian"
                      label="Is the child a natural born child, adopted child, step-child or under legal custody or guardianship of owner?"
                      value={props.formState.legalGuardian}
                      radios={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                      ]}
                      onChange={(e) => props.onFieldChange('legalGuardian', e.target.value)}
                    />
                  </div>
                </Fragment>
              )}
              <BackNext onBack={props.onBack} />
            </form>
          </FormProvider>
        </Col>
      </Row>
    </Container>
  );
}

InsuredInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

InsuredInfo.ValidationSchema = (currentAge) => {
  return object().shape({
    agreeToTerms: mixed().oneOf([true], 'You must agree to the privacy policy.'),
    firstName: customValidators.string().isValidNameLimitedSpecialChar('First Name is required.', true, false),
    middleName: customValidators.string().isValidNameLimitedSpecialChar('', false, false),
    lastName: customValidators.string().isValidNameLimitedSpecialChar('Last Name is required.', true, false),
    streetAddress: customValidators.string().isValidNameLimitedSpecialChar('Street Address is required.', true, true),
    apartment: customValidators.string().isValidNameLimitedSpecialChar('', false, true),
    city: customValidators.string().isValidNameLimitedSpecialChar('City is required', true, false),
    state: string().required('State is required.'),
    county: string().when('state', (state, schema) => {
      return state[0] === "FL" ? schema.required('County is required.') : schema.notRequired();
    }),
    zip: string()
      .required('Zip code is required.')
      .matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
    phone: string()
      .required('Phone number is required.')
      .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
    email: string().required('Email address is required.').email('Email address must be valid.'),
    driversLicense: customValidators.string().when('isJuvenile', (isJuvenile, schema) => {
      return isJuvenile[0]
        ? schema.notRequired()
        : schema.when('driversLicenseState', (driversLicenseState, innerSchema) =>
          innerSchema.ValidateDriversLicense('Invalid format for selected state.', driversLicenseState[0])
            .required("Driver's License Number is required.")
        );
    }),
    driversLicenseState: string().when('isJuvenile', (isJuvenile, schema) => {
      return isJuvenile[0] ? schema.notRequired() : schema.required("Driver's License State is required.");
    }),
    driversLicenseExpiration: date().transform(emptyStringToUndefined).when('isJuvenile', (isJuvenile, schema) => {
      return isJuvenile[0]
        ? schema.notRequired()
        : schema.typeError('Invalid date.')
          .min(moment().subtract(1, 'days'), 'Expiration date cannot be in the past.')
          .required('Expiration date is required');
    }),
    socialSecurityNumber: string()
      .required('Social Security Number is required.')
      .matches(/\d{3}-\d{2}-\d{4}/, 'Social Security Number must be formatted as "123-45-6789".'),
    ownerBirthdate: date().transform(emptyStringToUndefined).when('isJuvenile', (isJuvenile, schema) => {
      return isJuvenile[0]
        ? schema
          .typeError('Invalid date.')
          .required('Date of Birth is required.')
          .max(sub(new Date(), { years: currentAge + 1 }), `Owner's age cannot be smaller than child.`)
        : schema.notRequired();
    }),
    ownerGender: string().when('isJuvenile', (isJuvenile, schema) =>
      isJuvenile[0] ? schema.required('Gender is required.') : schema.notRequired()
    ),
    childFirstName: customValidators.string().when('isJuvenile', (isJuvenile, schema) => {
      return isJuvenile[0] ? schema.isValidNameLimitedSpecialChar('First Name is required.', true, false) : schema.notRequired()
    }
    ),
    childMiddleName: string().when('isJuvenile', (isJuvenile, schema) =>
      isJuvenile[0] ? schema.isValidNameLimitedSpecialChar('Middle Name is required.', true, false) : schema.notRequired()
    ),
    childLastName: string().when('isJuvenile', (isJuvenile, schema) =>
      isJuvenile[0] ? schema.isValidNameLimitedSpecialChar('Last Name is required.', true, false) : schema.notRequired()
    ),
    childSocialSecurityNumber: string().when('isJuvenile', (isJuvenile, schema) => {
      return isJuvenile[0]
        ? schema
          .required('Social Security Number is required.')
          .matches(/\d{3}-\d{2}-\d{4}/, 'Social Security Number must be formatted as "123-45-6789".')
        : schema.notRequired();
    }),
    legalGuardian: string().when('isJuvenile', (isJuvenile, schema) =>
      isJuvenile[0] ? schema.required('This field is required.') : schema.notRequired()
    ),
  });
};

InsuredInfo.DefaultFormState = (prefill = false) => {
  return {
    agreeToTerms: prefill ? true : false,
    pin: '0000',
    firstName: prefill ? 'Reymundo' : '',
    middleName: prefill ? 'J' : '',
    lastName: prefill ? `MATCH${makeid(10)}` : '',
    streetAddress: prefill ? '123 Main Street' : '',
    apartment: prefill ? '#123' : '',
    city: prefill ? 'Boston' : '',
    county: prefill ? 'Glenn' : '',
    state: prefill ? 'MA' : '',
    zip: prefill ? '02110' : '',
    phone: prefill ? '(123) 345-4567' : '',
    email: prefill ? 'reymundo.puga@example.com' : '',
    driversLicense: prefill ? '123a4567b' : '',
    driversLicenseState: prefill ? 'MA' : '',
    driversLicenseExpiration: prefill ? '05/05/2026' : '',
    socialSecurityNumber: prefill ? '123-12-1234' : '',
    ownerBirthdate: prefill ? '11/15/1988' : '',
    ownerGender: prefill ? 'M' : '',
    childFirstName: prefill ? 'Junior' : '',
    childMiddleName: prefill ? 'R' : '',
    childLastName: prefill ? `MATCHjunior${makeid(10)}` : '',
    childSocialSecurityNumber: prefill ? '123-02-1239' : '',
    legalGuardian: prefill ? 'true' : '',
  };
};

function makeid(l) {
  var text = "";
  var char_list = "abcdefghijklmnopqrstuvwxyz";
  for (var i = 0; i < l; i++) {
    text += char_list.charAt(Math.floor(Math.random() * char_list.length));
  }
  return text;
}
